import React, { useEffect, useRef, useState, lazy, Suspense, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

// Auth
import { isUserLoggedIn } from "./../../../../../../@core/auth/utils";

// Redux
import { setHomeMenuType, setHideHomeFooter } from "../../../../../../@core/redux/actions/home/index";

// CSS
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";

/* =========================
 * Lazy Components
 * ========================= */
const MenuGameContent = lazy(() => import("./Game"));
const MenuCampContent = lazy(() => import("./Camp"));
const MenuLotteryContent = lazy(() => import("./Lottery"));

/* =========================
 * Helper
 * ========================= */
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        "aria-controls": `vertical-tabpanel-${index}`,
    };
}

function LinkTab(props) {
    return (
        <Tab
            component="a"
            onClick={(event) => {
                event.preventDefault();
            }}
            {...props}
        />
    );
}

function ContentCategory({ processing, loadComponent, setLoadComponent, dataCampAll, checkPageGameList = false }) {
    const dataSetting = useSelector((state) => state.reduxAppMenu.menu_items);
    const dataMenu = useSelector((state) => state.reduxHome.menu);
    const reduxAppCMS = useSelector((state) => state.reduxAppCMS.cms);
    const homeData = useSelector(state => state.reduxHome?.result);

    const { categorySelect } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
    const dispatch = useDispatch();

    const scrollRef = useRef(null);

    const [value, setValue] = useState(0);
    const [menuList, setMenuList] = useState(null);

    const renderSizeNavMenuGame = useCallback(() => {
        const loggedIn = (isUserLoggedIn() || homeData);

        if (loggedIn) {
            return isLargeScreen ? 230 : 135;
        }

        return isLargeScreen ? 102 : 75;
    }, [isLargeScreen, homeData]);

    const navMenuHeight = renderSizeNavMenuGame();

    const useStyles = makeStyles((theme) => ({
        contentPosition: {
            width: "100%",
            height: "100%",
        },
        cardBody: {},
        cardOther: {
            [theme.breakpoints.down("md")]: {
                paddingLeft: "10px",
                paddingRight: "10px",
            },
            [theme.breakpoints.down("xs")]: {
                paddingLeft: "6px",
                paddingRight: "6px",
            },
        },

        heightMenu: {
            height: "100%",
        },

        widthMenuLeft: {
            width: "15%",
            [theme.breakpoints.down("md")]: { width: "20%" },
            [theme.breakpoints.down("sm")]: { width: "22%" },
        },

        widthMenuRight: {
            width: "85%",
            [theme.breakpoints.down("md")]: { width: "80%" },
            [theme.breakpoints.down("sm")]: { width: "78%" },
        },

        menuContent: {
            width: "100%",
            height: "100%",
        },

        lazyFallback: {
            width: "100%",
            padding: "12px 0",
        },

        /* =========================
         * ✅ Sticky Tabs (ติดใต้ navbar 82px)
         * ========================= */
        tabsStickyWrap: {
            position: "fixed",
            top: navMenuHeight,
            alignSelf: "flex-start",
            height: `calc(100vh - ${navMenuHeight}px)`,
            overflow: "hidden",
            zIndex: 10,
            width: "7%",
            [theme.breakpoints.down("lg")]: {
                width: "12%",
            },
            [theme.breakpoints.down("md")]: {
                width: "18%",
            },
            [theme.breakpoints.down("sm")]: {
                width: "20%",
                height: `calc(90vh - ${navMenuHeight}px)`,
            },
        },

        // ให้ Tabs scroll ภายในตัวเอง
        tabs: {
            height: "100%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
        },
    }));

    const classes = useStyles();

    const selectPageHome = useCallback(
        (item) => {
            if (!item?.code || item?.code === "home") {
                history.push("/");
                return;
            }
            history.push(`/game/${item.code}`);
        },
        [history]
    );

    const renderPageTabs = useCallback(() => {
        const isGameType = dataMenu?.typeGame !== 6 && dataMenu?.typeGame !== 10 && dataMenu?.typeGame !== 11 && dataMenu?.typeGame !== 13 && dataMenu?.typeGame !== 15;

        if (isGameType) {
            return <MenuGameContent dataCampAll={dataCampAll} dataMenu={dataMenu} />;
        }
        else if (!isGameType && dataMenu?.typeGame === 13) {
            return <MenuLotteryContent dataCampAll={dataCampAll} dataMenu={dataMenu} />;
        }

        return <MenuCampContent dataCampAll={dataCampAll} isLargeScreen={isLargeScreen} />;
    }, [dataMenu?.typeGame, dataCampAll]);

    useEffect(() => {
        if (processing && loadComponent === false) {
            setLoadComponent(true);
        }
    }, [processing, loadComponent, setLoadComponent]);

    // ✅ ถ้าไม่มี menu_camp -> redirect "/"
    useEffect(() => {
        const menuArr = dataSetting?.menu_camp;
        const hasMenu = Array.isArray(menuArr) && menuArr.length > 0;

        if (!hasMenu) {
            if (history.location.pathname !== "/") history.replace("/");
            setMenuList(null);
            return;
        }

        setMenuList(menuArr);
    }, [dataSetting?.menu_camp, history]);

    // ✅ ถ้ามีเมนู แต่ categorySelect ไม่มี/ไม่ตรง -> redirect "/"
    useEffect(() => {
        if (!menuList || menuList.length === 0) return;

        if (!categorySelect) {
            if (history.location.pathname !== "/") history.replace("/");
            return;
        }

        const indexFind = menuList.findIndex((item) => item?.code === categorySelect);
        if (indexFind < 0) {
            if (history.location.pathname !== "/") history.replace("/");
            return;
        }

        const pickedItem = menuList[indexFind];

        setValue(indexFind);
        window.scrollTo(0, 0);
        document.title = process.env.REACT_APP_OG_SITE_NAME + " - " + (pickedItem?.game_camp_type_name || pickedItem?.name);

        dispatch(
            setHomeMenuType({
                id: 0,
                renderPage: pickedItem.code,
                pageTitle: pickedItem.title,
                game_camp_image: pickedItem.game_camp_image,
                column: {
                    desktop: pickedItem.column_desktop,
                    mobile: pickedItem.column_mobile,
                },
                typeGame: pickedItem.game_camp_type,
            })
        );
    }, [menuList, categorySelect, dispatch, history]);

    const handleChange = useCallback(
        (event, newValue) => {
            setValue(newValue);
            const item = menuList?.[newValue];
            if (item) selectPageHome(item);
        },
        [menuList, selectPageHome]
    );

    useEffect(() => {
        dispatch(setHideHomeFooter(false));
    }, []);

    if (!menuList) return null;

    return (
        <div className={classes.contentPosition}>
            <div className="boxGame">
                <div
                    className={classes.cardBody}
                    ref={scrollRef}
                    style={{ background: reduxAppCMS !== null ? reduxAppCMS["bg-foter"] : "black" }}
                >
                    <div className={classes.cardOther}>
                        <Grid container spacing={isLargeScreen ? 2 : 1}>
                            {/* ✅ LEFT: Sticky Tabs */}
                            <Grid item className={[classes.heightMenu, classes.widthMenuLeft].join(" ")}>
                                <div className={classes.tabsStickyWrap}>
                                    <Tabs
                                        orientation="vertical"
                                        variant="scrollable"
                                        value={value}
                                        onChange={handleChange}
                                        className={classes.tabs}
                                    >
                                        {menuList.map((item, index) => (
                                            <LinkTab
                                                key={item?.code || index}
                                                label={
                                                    item?.image ? (
                                                        <div className="homeMenuIconFullPadding">
                                                            <img className="homeMenuIconFull" src={item.image} alt={item.name} />
                                                        </div>
                                                    ) : (
                                                        <div className="homeMenuText">{item.name}</div>
                                                    )
                                                }
                                                {...a11yProps(index)}
                                                onClick={() => selectPageHome(item)}
                                            />
                                        ))}
                                    </Tabs>
                                </div>
                            </Grid>

                            {/* RIGHT */}
                            <Grid item className={classes.widthMenuRight}>
                                <div className={(checkPageGameList && isUserLoggedIn()) ? "categoryGameCampPosition" : ""}>
                                    <div className={classes.menuContent}>
                                        {dataMenu !== null && (
                                            <Suspense fallback={null}>
                                                {renderPageTabs()}
                                            </Suspense>
                                        )}
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                </div>
            </div>
        </div>
    );
}

ContentCategory.propTypes = {
    processing: PropTypes.bool,
    loadComponent: PropTypes.bool,
    setLoadComponent: PropTypes.func,
    dataCampAll: PropTypes.any,
    checkPageGameList: PropTypes.bool,
};

export default memo(ContentCategory);
